import React, { useState, useEffect, useCallback } from 'react';
import { Form, InputNumber, Switch, Button, Card, message, Space, Select } from 'antd';
import { makeGetCall, makePostCall } from '../../utils/helper';
import './add-pricing.scss';

const AddPricing = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Dropdown data: {label, value, id} objects from API
  const [centres, setCentres] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [allBodyParts, setAllBodyParts] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);

  // Fetch centres, modalities, and body parts on mount (in parallel)
  useEffect(() => {
    const fetchCentres = makeGetCall('/centres?active=true')
      .then((res) => { if (res.data.success) setCentres(res.data.data || []); })
      .catch((err) => console.error('Failed to fetch centres:', err));

    const fetchModalities = makeGetCall('/modalities')
      .then((res) => { if (res.data.success) setModalities(res.data.data || []); })
      .catch((err) => console.error('Failed to fetch modalities:', err));

    const fetchBodyParts = makeGetCall('/body-parts')
      .then((res) => { if (res.data.success) setAllBodyParts(res.data.data || []); })
      .catch((err) => console.error('Failed to fetch body parts:', err));

    Promise.all([fetchCentres, fetchModalities, fetchBodyParts]);
  }, []);

  // Filter body parts when modality changes
  const handleModalityChange = useCallback((modality) => {
    form.setFieldValue('cp_body_part', undefined);
    if (!modality) {
      setBodyParts([]);
      return;
    }
    const filtered = allBodyParts.filter((bp) => bp.modality === modality);
    setBodyParts(filtered);
  }, [allBodyParts, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await makePostCall('/centre-pricing', {
        ...values,
        cp_active: values.cp_active !== undefined ? values.cp_active : true,
      });

      if (response.data.success) {
        message.success('Pricing rule created successfully');
        form.resetFields();
        setBodyParts([]);
      } else {
        throw new Error(response.data.message || 'Failed to create pricing rule');
      }
    } catch (error) {
      message.error(error.message || 'Failed to create pricing rule');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setBodyParts([]);
  };

  return (
    <div className="admin-container add-pricing-container">
      <div className="admin-header">
        <h2>Add Pricing Rule</h2>
      </div>

      <Card className="add-pricing-card">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="admin-form add-pricing-form"
          initialValues={{
            cp_active: true,
          }}
        >
          <div className="form-grid">
            <Form.Item
              label="Centre"
              name="cp_centre_code"
              rules={[{ required: true, message: 'Please select a centre' }]}
            >
              <Select placeholder="Select centre" size="large" showSearch optionFilterProp="children">
                {centres.map((c) => (
                  <Select.Option key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Modality"
              name="cp_modality"
              rules={[{ required: true, message: 'Please select a modality' }]}
            >
              <Select
                placeholder="Select modality"
                size="large"
                showSearch
                optionFilterProp="children"
                onChange={handleModalityChange}
              >
                {modalities.map((m) => (
                  <Select.Option key={m.value} value={m.value}>
                    {m.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Body Part"
              name="cp_body_part"
              rules={[{ required: true, message: 'Please select a body part' }]}
            >
              <Select placeholder="Select body part" size="large" showSearch optionFilterProp="children">
                {bodyParts.map((bp) => (
                  <Select.Option key={bp.value} value={bp.value}>
                    {bp.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Price"
              name="cp_price"
              rules={[{ required: true, message: 'Please enter a price' }]}
            >
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                style={{ width: '100%' }}
                placeholder="Enter price"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="Active Status"
              name="cp_active"
              valuePropName="checked"
              tooltip="Toggle to set the pricing rule as active or inactive"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" defaultChecked />
            </Form.Item>
          </div>

          <Form.Item className="form-actions">
            <Space size="middle">
              <Button size="large" onClick={handleReset}>
                Reset
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} size="large">
                Create Pricing Rule
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default AddPricing;
